import React, { useContext, useMemo, useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    TextField,
    Tooltip,
    MenuItem,
} from '@material-ui/core';
import { useMutation } from '@apollo/client';
import { get } from 'lodash';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PageTitle, PrimaryButton } from '@/styles/blocks';
import { setImpersonatedClientId } from '@/utilities/impersonation';
import { useClientUser } from '@/hooks';
import { UPDATE_CLIENT_BY_PK } from '@/graphql/mutations/clients';
import { SET_USER_CIRCLE } from '@/graphql/mutations/users';
import { UserContext } from '../App';
import { useNavigate } from 'react-router';
import SearchInput from '../misc/SearchInput';
import { greys, white } from '@/styles/colors';
import CorpAdminStore, { CorpAdminContext, MARKETPLACE_KEY } from './store';
import { filterContractorRows, visibleTabs } from './utils';
import { DISABLE_SHIPPER_RATE_SHOP_OVERRIDES } from './graphql';
import CarrierRow from './modals/CarrierRow';
import RowActionsMenu from './modals/RowActionsMenu';
import { CopyableId } from './modals/blocks';
import StatusFilter from './modals/StatusFilter';
import OptionFilter from './modals/OptionFilter';
import { CARRIER_TYPE_LABELS, SHIPPER_TYPE_LABELS, SUPPLY_CHAIN_LEG_LABELS } from './modals/constants';
import { evaluateDocStatus } from './modals/DocStatusIcon';
import ConnectCarrierModal from './modals/ConnectCarrierModal';
import CreateClientModal from './modals/CreateClientModal';
import AccountDetailsModal from './modals/AccountDetailsModal';
import { CREATE_SHIPPER_CONFIG, CREATE_FLEX_CARRIER_CONFIG } from './modals/createClientConfigs';
import Snackbar from '../Snackbar';

const TAB_ACTIONS = {
    SHIPPER: { label: 'Create Shipper', kind: 'create', config: CREATE_SHIPPER_CONFIG },
    FLEX: { label: 'Create Carrier', kind: 'create', config: CREATE_FLEX_CARRIER_CONFIG },
    NETWORK: { label: 'Connect Carrier', kind: 'connectCarrier' },
};

const PROGRESS_FILTER_LABELS = { red: 'Not Started', orange: 'In Progress', green: 'Complete' };

const ORG_DOC_TYPES = ['AGREEMENT', 'COI', 'W9'];

const labelMapToOptions = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

// Maps a header label → filter spec. `kind` selects the filter component:
// "level" → StatusFilter (traffic-light), "options" → OptionFilter (single-select enum).
const NETWORK_FILTERABLE_HEADERS = {
    Type: { key: 'carrier_type', kind: 'options', options: labelMapToOptions(CARRIER_TYPE_LABELS) },
    Leg: { key: 'supply_chain_leg', kind: 'options', options: labelMapToOptions(SUPPLY_CHAIN_LEG_LABELS) },
    Documents: { key: 'documents', kind: 'level', labels: PROGRESS_FILTER_LABELS },
};

const SHIPPER_FILTERABLE_HEADERS = {
    Type: { key: 'shipper_type', kind: 'options', options: labelMapToOptions(SHIPPER_TYPE_LABELS) },
};

const filterableHeadersFor = (tab) => {
    if (tab?.assetType === 'light') return NETWORK_FILTERABLE_HEADERS;
    if (tab?.kind === 'contractor') return {};
    if (!tab?.assetType) return SHIPPER_FILTERABLE_HEADERS;
    return {};
};

// Aggregates the three org doc types into a single progress level:
// green = all 3 valid, red = no docs on file, orange = anything in between.
const docsAggregateLevel = (carrierDocs) => {
    const states = ORG_DOC_TYPES.map((t) => evaluateDocStatus(carrierDocs?.[t]).state);
    const validCount = states.filter((s) => s === 'valid').length;
    const presentCount = states.filter((s) => s !== 'missing').length;
    if (validCount === ORG_DOC_TYPES.length) return 'green';
    if (presentCount === 0) return 'red';
    return 'orange';
};

const SEARCHABLE = ['email', 'business_name', 'client_id'];

// `kind` rather than a third assetType — assetType drives the carrier map lookup and column set,
// and a contractor value there would resolve to a null carrierMap.
const ALL_TABS = [
    { header: 'Shippers', kind: 'shipper', role: 'SHIPPER', assetType: null, action: TAB_ACTIONS.SHIPPER },
    { header: 'Flex Centers', kind: 'carrier', role: 'CARRIER', assetType: 'heavy', action: TAB_ACTIONS.FLEX },
    {
        header: 'Fulfillment Partners',
        kind: 'carrier',
        role: 'CARRIER',
        assetType: 'light',
        action: TAB_ACTIONS.NETWORK,
    },
    { header: 'Motor Carriers', kind: 'contractor', role: 'CONTRACTOR', assetType: null, action: null },
].map((tab, index) => ({ ...tab, index }));

const SHIPPER_TYPE_WIDTH = 180;

const SHIPPER_COLUMNS = [
    { label: 'Account Name', value: (c) => c?.business_name || '--' },
    { label: 'Primary Email', value: (c) => c?.email || '--', copyable: true },
    { label: 'Account ID', value: (c) => c?.client_id || '--', copyable: true },
];

const FLEX_CARRIER_COLUMNS = [
    { label: 'Account Name' },
    { label: 'Primary Email' },
    { label: 'Account ID' },
    { label: '', width: 80 },
];

const NETWORK_CARRIER_COLUMNS = [
    { label: 'Account Details' },
    { label: 'Type', width: 120 },
    { label: 'Leg', width: 160 },
    { label: 'Airport Codes', width: 200 },
    { label: 'Documents', width: 220 },
    { label: '', width: 80 },
];

const carrierColumnsFor = (assetType) => (assetType === 'light' ? NETWORK_CARRIER_COLUMNS : FLEX_CARRIER_COLUMNS);

const CONTRACTOR_ACTIONS_WIDTH = 80;

// Percentages rather than px so the columns shrink together (see fluidWidth). The four content columns
// share what's left after the pinned actions column, so each subtracts its quarter of that 80px — the
// row still sums to exactly 100%.
const CONTRACTOR_COLUMNS = [
    {
        label: 'Account Name',
        width: `calc(24% - ${CONTRACTOR_ACTIONS_WIDTH / 4}px)`,
        value: (row) => row?.contractor?.business_name || '--',
    },
    {
        label: 'Primary Email',
        width: `calc(26% - ${CONTRACTOR_ACTIONS_WIDTH / 4}px)`,
        value: (row) => row?.contractor?.email || '--',
        copyable: true,
    },
    {
        label: 'Account ID',
        width: `calc(24% - ${CONTRACTOR_ACTIONS_WIDTH / 4}px)`,
        value: (row) => row?.client_id || '--',
        copyable: true,
    },
    { label: 'Flex Centers', width: `calc(26% - ${CONTRACTOR_ACTIONS_WIDTH / 4}px)` },
    { label: '', width: CONTRACTOR_ACTIONS_WIDTH, actions: true },
];

// The copy pill is an inline-flex button around a uuid/email, neither of which has a break
// opportunity — without this it overflows into the neighbouring column rather than shrinking with its
// cell. Ellipsize instead; the full value stays in the tooltip and one click away.
const contractorCellCss = css`
    overflow: hidden;
    overflow-wrap: anywhere;

    & button {
        max-width: 100%;
        min-width: 0;
    }

    & button > span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

// FixedTable is table-layout: fixed, so a pinned column cannot give up space and its neighbours absorb
// every bit of shrink. Percentage columns stay fluid and shrink together.
const pinnedPxWidth = (px) => css`
    width: ${px}px;
    min-width: ${px}px;
    max-width: ${px}px;
`;

const fluidWidth = (value) => css`
    width: ${value};
`;

const HeaderCell = styled.span`
    font-size: 16px;
    font-weight: 600;
`;

const FixedTable = styled(Table)`
    table-layout: fixed;
    width: 100%;

    & .MuiTableCell-stickyHeader {
        background: ${white.primary};
        top: 0;
        z-index: 10;
    }
`;

// Circle flag the ingest pipeline actually reads to auto-assign by consignee zip.
const AUTO_ASSIGN_BY_ZIP_FLAG = 'auto-assign-by-zip';

const ShipperTypeSelect = ({ client, onError, onSuccess }) => {
    const [updateClient, { loading: clientLoading }] = useMutation(UPDATE_CLIENT_BY_PK);
    const [disableOverrides, { loading: disableLoading }] = useMutation(DISABLE_SHIPPER_RATE_SHOP_OVERRIDES);
    const [setCircle, { loading: setLoading }] = useMutation(SET_USER_CIRCLE);
    const value = client?.shipper_type || 'MARKETPLACE';
    const loading = clientLoading || disableLoading || setLoading;

    const handleChange = async (e) => {
        const next = e.target.value;
        if (next === value) return;
        try {
            // Leaving Rate Shop disables this shipper's pricing overrides so stale,
            // shipper-driven rates don't keep applying under the new mode.
            if (value === 'RATE_SHOP' && next !== 'RATE_SHOP') {
                await disableOverrides({ variables: { client_id: client.client_id } });
            }
            await updateClient({
                variables: { client_id: client.client_id, update: { shipper_type: next } },
            });
            // shipper_type is only a label; the pipeline gates on the auto-assign-by-zip
            // circle, so entering/leaving AUTO_ASSIGN must flip it or the mode never runs.
            const user_id = client?.user?.user_id;
            if (user_id && (next === 'AUTO_ASSIGN' || value === 'AUTO_ASSIGN')) {
                await setCircle({
                    variables: { user_id, patch: { [AUTO_ASSIGN_BY_ZIP_FLAG]: next === 'AUTO_ASSIGN' } },
                });
            }
            onSuccess?.(next);
        } catch (err) {
            onError?.(err);
        }
    };

    return (
        <TextField
            select
            size="small"
            variant="outlined"
            disabled={loading}
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            css={css`
                min-width: 140px;
            `}
        >
            {Object.entries(SHIPPER_TYPE_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>
                    {label}
                </MenuItem>
            ))}
        </TextField>
    );
};

const CorporateUsersInner = () => {
    const { admin_clients, setClientId } = useContext(UserContext);
    const { organization_id } = useClientUser();
    const { state, callbacks } = useContext(CorpAdminContext);
    const { assetHeavy, assetLight, notification, corpDocsByCarrier, contractors } = state;
    const { setNotification } = callbacks;
    const navigate = useNavigate();

    const tabs = useMemo(() => visibleTabs(ALL_TABS, { contractors }), [contractors]);

    const [tabIndex, setTabIndex] = useState(0);
    const tab = tabs[tabIndex] || tabs[0];
    const [search, setSearch] = useState('');
    const [expandedCarrierId, setExpandedCarrierId] = useState(null);
    const [statusFilters, setStatusFilters] = useState({});
    const [createConfig, setCreateConfig] = useState(null);
    const [connectCarrierOpen, setConnectCarrierOpen] = useState(false);
    // One modal for the page. The target outlives the close so the tabs don't swap mid-transition.
    const [detailsTarget, setDetailsTarget] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const openDetails = (target) => {
        setDetailsTarget(target);
        setDetailsOpen(true);
    };

    useEffect(() => {
        if (tabIndex >= tabs.length) setTabIndex(0);
    }, [tabIndex, tabs.length]);

    useEffect(() => {
        setExpandedCarrierId(null);
    }, [tabIndex, search]);

    useEffect(() => {
        // Each tab has its own filter set; drop them on switch.
        setStatusFilters({});
    }, [tabIndex]);

    const setFilter = (key, level) => setStatusFilters((prev) => ({ ...prev, [key]: level }));

    const isCarrierTab = tab?.kind === 'carrier';
    const isContractorTab = tab?.kind === 'contractor';
    const carrierMap = tab?.assetType === 'heavy' ? assetHeavy : tab?.assetType === 'light' ? assetLight : null;

    const shipperClients = useMemo(() => {
        if (isCarrierTab || isContractorTab) return [];
        return (admin_clients || [])
            .filter((client) => {
                if (!client?.user?.roles?.['SHIPPER']) return false;
                if (statusFilters.shipper_type) {
                    const val = client?.shipper_type || 'MARKETPLACE';
                    if (val !== statusFilters.shipper_type) return false;
                }
                if (search) {
                    const q = search.toLowerCase();
                    return SEARCHABLE.some((field) => {
                        const value = get(client, field);
                        return value && value.toString().toLowerCase().includes(q);
                    });
                }
                return true;
            })
            .sort((a, b) => (a?.business_name || '').localeCompare(b?.business_name || ''));
    }, [admin_clients, search, isCarrierTab, isContractorTab, statusFilters]);

    const contractorRows = useMemo(
        () => (isContractorTab ? filterContractorRows(contractors || [], search) : []),
        [isContractorTab, contractors, search]
    );

    const isNetworkTab = tab?.assetType === 'light';
    const hasActiveFilter = isNetworkTab && Object.values(statusFilters).some(Boolean);
    const filterableHeaders = filterableHeadersFor(tab);

    const carrierIds = useMemo(() => {
        if (!carrierMap) return [];
        let ids = Object.keys(carrierMap);
        if (search) {
            ids = ids.filter((carrier_id) => {
                if (carrier_id === MARKETPLACE_KEY)
                    return 'Onward Marketplace'.toLowerCase().includes(search.toLowerCase());
                const carrier = carrierMap[carrier_id]?.carrier;
                const q = search.toLowerCase();
                return SEARCHABLE.some((field) => {
                    const value = get(carrier, field);
                    return value && value.toString().toLowerCase().includes(q);
                });
            });
        }

        if (hasActiveFilter) {
            ids = ids.filter((carrier_id) => {
                // Marketplace has no carrier-level fields to evaluate, so it's filtered out
                // whenever any column filter is active.
                if (carrier_id === MARKETPLACE_KEY) return false;
                const carrier = carrierMap[carrier_id]?.carrier;
                const carrierDocs = corpDocsByCarrier?.[carrier_id] || {};

                if (statusFilters.carrier_type && carrier?.carrier_type !== statusFilters.carrier_type) return false;
                if (
                    statusFilters.supply_chain_leg &&
                    !carrier?.supply_chain_legs?.includes(statusFilters.supply_chain_leg)
                )
                    return false;
                if (statusFilters.documents && docsAggregateLevel(carrierDocs) !== statusFilters.documents)
                    return false;
                return true;
            });
        }

        return ids.sort((a, b) => {
            // Marketplace always pinned to the top.
            if (a === MARKETPLACE_KEY) return -1;
            if (b === MARKETPLACE_KEY) return 1;
            const nameA = carrierMap[a]?.carrier?.business_name || '';
            const nameB = carrierMap[b]?.carrier?.business_name || '';
            return nameA.localeCompare(nameB);
        });
    }, [carrierMap, search, hasActiveFilter, statusFilters, corpDocsByCarrier]);

    const signInAsUser = (client) => {
        setImpersonatedClientId(client.client_id);
        setClientId(client.client_id);
    };

    return (
        <Grid
            container
            css={css`
                flex-direction: column;
                flex-wrap: nowrap;
                height: 100%;
            `}
        >
            <Snackbar
                open={!!notification.message}
                {...notification}
                handleClose={() => setNotification({})}
                autohide={4000}
            />
            <CreateClientModal
                open={!!createConfig}
                onClose={() => setCreateConfig(null)}
                config={createConfig || CREATE_SHIPPER_CONFIG}
            />
            <ConnectCarrierModal open={connectCarrierOpen} onClose={() => setConnectCarrierOpen(false)} />
            <AccountDetailsModal
                open={detailsOpen}
                context={detailsTarget?.context}
                clientId={detailsTarget?.clientId}
                clientName={detailsTarget?.clientName}
                onClose={() => setDetailsOpen(false)}
            />

            <Grid
                item
                css={css`
                    background: ${white.primary};
                    border-bottom: 1px solid ${greys.border};
                    display: flex;
                    justify-content: center;
                `}
            >
                <Tabs
                    textColor="primary"
                    indicatorColor="primary"
                    value={tabIndex}
                    onChange={(_, newTab) => setTabIndex(newTab)}
                    centered
                >
                    {tabs.map((t) => (
                        <Tab label={t.header} key={t.header} />
                    ))}
                </Tabs>
            </Grid>

            <Grid
                item
                css={css`
                    padding: 2rem 5rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                `}
            >
                <PageTitle
                    css={css`
                        margin: 0;
                        flex-grow: 1;
                    `}
                >
                    Accounts
                </PageTitle>
                <SearchInput onChange={(value) => setSearch(value)} placeholder="Search Accounts" />
                {tab?.action && (
                    <Tooltip
                        title={
                            !organization_id
                                ? 'Your account is not associated with an organization — contact support.'
                                : ''
                        }
                    >
                        {/* span wrapper so Tooltip can attach to a disabled button */}
                        <span>
                            <PrimaryButton
                                onClick={() => {
                                    if (tab.action.kind === 'create') {
                                        setCreateConfig(tab.action.config);
                                    } else if (tab.action.kind === 'connectCarrier') {
                                        setConnectCarrierOpen(true);
                                    }
                                }}
                                disabled={!organization_id}
                                css={css`
                                    white-space: nowrap;
                                `}
                            >
                                {tab.action.label}
                            </PrimaryButton>
                        </span>
                    </Tooltip>
                )}
            </Grid>

            <Grid
                item
                css={css`
                    padding: 0 5rem 5rem;
                    display: flex;
                    flex-grow: 1;
                    overflow-y: hidden;
                `}
            >
                <Paper
                    css={css`
                        width: 100%;
                        height: 100% !important;
                        overflow-y: scroll;
                        overflow-x: hidden;
                        border: none;
                        border-radius: 0;
                    `}
                >
                    <FixedTable size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {(isCarrierTab
                                    ? carrierColumnsFor(tab?.assetType)
                                    : isContractorTab
                                    ? CONTRACTOR_COLUMNS
                                    : [
                                          ...SHIPPER_COLUMNS.map((c) => ({ label: c.label })),
                                          { label: 'Type', width: SHIPPER_TYPE_WIDTH },
                                          { label: '', width: 80 },
                                      ]
                                ).map(({ label, width }) => {
                                    const filter = filterableHeaders[label];
                                    const widthCss = !width
                                        ? undefined
                                        : typeof width === 'number'
                                        ? pinnedPxWidth(width)
                                        : fluidWidth(width);
                                    return (
                                        <TableCell key={label} css={widthCss}>
                                            <span
                                                css={css`
                                                    display: inline-flex;
                                                    align-items: center;
                                                    gap: 4px;
                                                `}
                                            >
                                                <HeaderCell>{label}</HeaderCell>
                                                {filter?.kind === 'level' && (
                                                    <StatusFilter
                                                        value={statusFilters[filter.key] || null}
                                                        onChange={(level) => setFilter(filter.key, level)}
                                                        labels={filter.labels}
                                                    />
                                                )}
                                                {filter?.kind === 'options' && (
                                                    <OptionFilter
                                                        value={statusFilters[filter.key] || null}
                                                        onChange={(val) => setFilter(filter.key, val)}
                                                        options={filter.options}
                                                    />
                                                )}
                                            </span>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isContractorTab
                                ? contractorRows.map((row) => (
                                      <TableRow
                                          key={row.client_id}
                                          css={css`
                                              :hover {
                                                  background-color: ${greys.selected};
                                              }
                                          `}
                                      >
                                          {CONTRACTOR_COLUMNS.map((column) => {
                                              if (column.actions) {
                                                  return (
                                                      <TableCell key={`${row.client_id}-actions`} align="right">
                                                          <RowActionsMenu
                                                              items={[
                                                                  {
                                                                      key: 'details',
                                                                      label: 'Details',
                                                                      onClick: () =>
                                                                          openDetails({
                                                                              context: 'CONTRACTOR',
                                                                              clientId: row.client_id,
                                                                              clientName: row.contractor?.business_name,
                                                                          }),
                                                                  },
                                                              ]}
                                                          />
                                                      </TableCell>
                                                  );
                                              }
                                              if (column.label === 'Flex Centers') {
                                                  return (
                                                      <TableCell key={`${row.client_id}-flex`} css={contractorCellCss}>
                                                          {row.flexCenters.length === 0
                                                              ? '--'
                                                              : row.flexCenters
                                                                    .map((f) => f.business_name || f.client_id)
                                                                    .join(', ')}
                                                      </TableCell>
                                                  );
                                              }
                                              const val = column.value(row);
                                              return (
                                                  <TableCell
                                                      key={`${row.client_id}-${column.label}`}
                                                      css={contractorCellCss}
                                                  >
                                                      {column.copyable ? (
                                                          <CopyableId label={column.label} value={val} />
                                                      ) : (
                                                          val
                                                      )}
                                                  </TableCell>
                                              );
                                          })}
                                      </TableRow>
                                  ))
                                : isCarrierTab
                                ? carrierIds.map((carrier_id) => (
                                      <CarrierRow
                                          key={carrier_id}
                                          carrier_id={carrier_id}
                                          carrierData={carrierMap[carrier_id]}
                                          assetType={tab?.assetType}
                                          onSignIn={signInAsUser}
                                          onDetails={openDetails}
                                          isExpanded={expandedCarrierId === carrier_id}
                                          onToggleExpand={() =>
                                              setExpandedCarrierId((prev) => (prev === carrier_id ? null : carrier_id))
                                          }
                                      />
                                  ))
                                : shipperClients.map((client) => (
                                      <TableRow
                                          key={client.client_id}
                                          css={css`
                                              :hover {
                                                  background-color: ${greys.selected};
                                              }
                                          `}
                                      >
                                          {SHIPPER_COLUMNS.map((column) => {
                                              const val = column.value(client);
                                              return (
                                                  <TableCell key={`${client.client_id}-${column.label}`}>
                                                      {column.copyable ? (
                                                          <CopyableId label={column.label} value={val} />
                                                      ) : (
                                                          val
                                                      )}
                                                  </TableCell>
                                              );
                                          })}
                                          <TableCell>
                                              <ShipperTypeSelect
                                                  client={client}
                                                  onSuccess={() =>
                                                      setNotification({
                                                          severity: 'success',
                                                          message: 'Shipper type updated',
                                                      })
                                                  }
                                                  onError={() =>
                                                      setNotification({
                                                          severity: 'error',
                                                          message: 'Failed to update shipper type',
                                                      })
                                                  }
                                              />
                                          </TableCell>
                                          <TableCell align="right">
                                              <RowActionsMenu
                                                  items={[
                                                      {
                                                          key: 'details',
                                                          label: 'Details',
                                                          onClick: () =>
                                                              openDetails({
                                                                  context: 'SHIPPER',
                                                                  clientId: client.client_id,
                                                                  clientName: client.business_name,
                                                              }),
                                                      },
                                                      {
                                                          key: 'sign-in',
                                                          label: 'Sign In',
                                                          onClick: () => signInAsUser(client),
                                                      },
                                                  ]}
                                              />
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </FixedTable>
                </Paper>
            </Grid>
        </Grid>
    );
};

const CorporateUsers = () => (
    <CorpAdminStore>
        <CorporateUsersInner />
    </CorpAdminStore>
);

export default CorporateUsers;
